// Phase APP-4 · Lead Worker Request status — /dashboard/micro-hub/worker-requests
// Role: micro_hub_lead. Read-only drawer (Lead cannot approve/reject their own
// request). "+ Request Packer" CTA. Resubmit available when status==rejected_1.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, Plus, RefreshCw } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import StatusBadge from "../../components/hub/StatusBadge";
import ApprovalDetailDrawer from "../../components/admin/approvals/ApprovalDetailDrawer";
import RequestPackerModal from "../../components/admin/approvals/RequestPackerModal";
import ResubmitRequestModal from "../../components/admin/approvals/ResubmitRequestModal";
import MicroLeadSubNav from "../../components/microlead/MicroLeadSubNav";

const ORANGE = "#FF6B00";
const BUILD_STAMP = "APP-1.0-r7";

function relTime(iso) {
  if (!iso) return "—";
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function MiniKpi({ label, value, color, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2"
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function Body() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");
  const [openId, setOpenId] = useState(urlId || null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [resubmitFor, setResubmitFor] = useState(null);

  const queryParams = useMemo(() => ({}), []);
  const q = useTimedFetch("/approvals/queue", { params: queryParams });

  useEffect(() => {
    const t = setInterval(q.reload, 30_000);
    return () => clearInterval(t);
  }, [q.reload]);

  useEffect(() => {
    if (openId) setSearchParams({ id: openId });
    else setSearchParams({});
  }, [openId, setSearchParams]);
  useEffect(() => {
    if (urlId) setOpenId(urlId);
    else setOpenId(null);
  }, [urlId]);

  const rows = q.data?.rows || [];
  const myPending = rows.filter((r) =>
    r.status === "pending" || r.status === "resubmitted_pending").length;
  const myRejected = rows.filter((r) => r.status === "rejected_1").length;
  const monthPrefix = new Date().toISOString().slice(0, 7);
  const myApprovedThisMonth = rows.filter((r) =>
    (r.status === "approved" || r.status === "founder_override_approved")
    && (r.submitted_at || "").startsWith(monthPrefix)).length;

  const onChanged = useCallback(() => q.reload(), [q]);

  return (
    <div>
      <MicroLeadSubNav/>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4"
           data-testid="lead-worker-requests-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900">Worker Requests</h1>
          <button onClick={() => setRequestOpen(true)}
                  data-testid="lead-request-packer-cta"
                  className="ml-auto text-sm font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5"
                  style={{ backgroundColor: ORANGE }}>
            <Plus className="h-4 w-4"/> Request Packer
          </button>
          <span data-testid="lead-wr-build-stamp"
                className="text-[10px] font-mono text-slate-400">
            {BUILD_STAMP}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="lead-kpis">
          <MiniKpi label="My pending"          value={myPending}          color={ORANGE}    testid="lead-kpi-pending"/>
          <MiniKpi label="Awaiting resubmit"   value={myRejected}         color="#EF4444"   testid="lead-kpi-rejected"/>
          <MiniKpi label="Approved this month" value={myApprovedThisMonth} color="#10B981"  testid="lead-kpi-approved"/>
        </div>

        {q.error ? (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm flex items-center"
               data-testid="lead-wr-error">
            <span className="flex-1">Couldn't load · {q.error.message}</span>
            <button onClick={q.reload}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                    style={{ backgroundColor: ORANGE }}>Retry</button>
          </div>
        ) : q.loading && rows.length === 0 ? (
          <div className="space-y-2" data-testid="lead-wr-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse"/>)}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-10 text-center"
               data-testid="lead-wr-empty">
            <RefreshCw className="h-6 w-6 text-slate-300 mx-auto mb-1"/>
            <div className="text-sm font-semibold text-slate-700">No worker requests yet</div>
            <button onClick={() => setRequestOpen(true)}
                    className="mt-2 text-xs text-[#FF6B00] hover:underline">
              + Request your first packer
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto"
               data-testid="lead-wr-queue-wrap">
            <table className="w-full text-sm min-w-[900px]" data-testid="lead-wr-queue-table">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <Th>Status</Th><Th>Packer</Th><Th>Phone</Th>
                  <Th>Shift</Th><Th>Submitted</Th>
                  <Th className="text-center">Resub</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const p = r.entity_payload || {};
                  const canResubmit = r.status === "rejected_1"
                                        && (r.resubmission_count ?? 0) < 1;
                  const isTerminal = r.status === "rejected_terminal";
                  return (
                    <tr key={r.request_id}
                        data-testid={`lead-wr-row-${r.request_id}`}
                        data-status={r.status}
                        className="hover:bg-orange-50/30">
                      <td className="px-3 py-2"><StatusBadge status={r.status} size="sm"/></td>
                      <td className="px-3 py-2 font-medium">{p.full_name || r.display_name}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{p.phone || "—"}</td>
                      <td className="px-3 py-2 capitalize">{p.shift_timing || "—"}</td>
                      <td className="px-3 py-2 text-xs" title={r.submitted_at}>{relTime(r.submitted_at)}</td>
                      <td className="px-3 py-2 text-center">{r.resubmission_count ?? 0}/1</td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <button onClick={() => setOpenId(r.request_id)}
                                data-testid={`lead-wr-view-${r.request_id}`}
                                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-orange-50">
                          <Eye className="h-3 w-3"/> View
                        </button>
                        {canResubmit && (
                          <button onClick={() => setResubmitFor(r)}
                                  data-testid={`lead-wr-resubmit-${r.request_id}`}
                                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg text-white"
                                  style={{ backgroundColor: ORANGE }}>
                            Resubmit
                          </button>
                        )}
                        {isTerminal && (
                          <button disabled
                                  title="Already terminally rejected — no further resubmits allowed."
                                  data-testid={`lead-wr-resubmit-${r.request_id}-disabled`}
                                  data-disabled-reason="terminal"
                                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-slate-400 cursor-not-allowed">
                            Resubmit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ApprovalDetailDrawer
        open={!!openId} requestId={openId}
        onClose={() => setOpenId(null)}
        readOnly={true}
        onChanged={onChanged}/>
      <RequestPackerModal open={requestOpen}
                            onClose={() => setRequestOpen(false)}
                            onDone={() => q.reload()}/>
      <ResubmitRequestModal open={!!resubmitFor}
                              onClose={() => setResubmitFor(null)}
                              request={resubmitFor}
                              onDone={() => { setResubmitFor(null); q.reload(); }}/>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 ${className}`}>{children}</th>;
}

export default function LeadWorkerRequests() {
  return (
    <RoleGate allow={["micro_hub_lead", "admin", "founder", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
